import React, { FC, useCallback, useEffect } from 'react';
import { Flex } from 'antd';
import { Modal } from '../../../common/components/modal/Modal';
import { ModalFooter } from '../../../common/components/modal/ModalFooter';
import { ModalHeader } from '../../../common/components/modal/ModalHeader';
import { ModalTitle } from '../../../common/components/modal/ModalTitle';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './AlgorithmFilterClauseFormModal.translations.json';
import { Padding } from '../../../common/components/Padding';
import { AlgorithmConditionForm } from '../algorithmCondition/AlgorithmConditionForm';
import { AlgorithmElementFunctionViewType } from '../../types';
import { useFormState } from 'react-hook-form';
import { useGetAlgorithmMappingsQuery } from '../mappings/query/useGetAlgorithmMappingsQuery';
import { useGetAlgorithmSourcesQuery } from '../sources/query/useGetAlgorithmSourcesQuery';
import {
  confirmPanelFormClose,
  Form,
  FormAlerts,
  useAlertContext,
} from '../../../common/form';
import { ActionButton } from '../../../common/components';
import { confirmationHeaders } from '../../../common/utils';
import { GetApiErrorMessage } from '../../../common/utils/ValidationMessagesMap';
import { useMutationErrors } from '../../../common/queryClient';
import validationTranslations from '../../translations/validation.translations.json';
import { useMutateAlgorithmFilterClauseQuery } from './query/useMutateAlgorithmFilterClauseQuery';
import { useGetAlgorithmFilterClauseQuery } from './query/useGetAlgorithmFilterClauseQuery';
import { AlgorithmSourceFormDataType } from '../sources/schema/types';
import type { AlgorithmMappingQueryDtoType } from '../mappings/schema/types';
import { AlgorithmFilterClauseValidFormDataSchema } from './schema/AlgorithmFilterClauseValidFormDataSchema';
import { AlgorithmFilterClauseValidFormDataType } from './schema/types';

const { addTranslations, t } = i18nNsFactory('algorithm-filter-clause-modal');
addTranslations(translations);
addTranslations(validationTranslations);

const translateMutationError: GetApiErrorMessage = (code, meta) =>
  t('alert.error.code.' + code, meta?.args && { ...meta.args });

export interface AlgorithmFilterClauseFormModalProps {
  algorithmId: number;
  onClose?: (changed: boolean) => void;
  onLoadingStateChange?: (isLoading: boolean) => void;
}

export const AlgorithmFilterClauseFormModal: FC<
  AlgorithmFilterClauseFormModalProps
> = (props) => {
  const { algorithmId, onClose, onLoadingStateChange } = props;

  const {
    data: algorithmFilterClause,
    isFetching: isFetchingFilterClause,
    refetch,
  } = useGetAlgorithmFilterClauseQuery({
    id: algorithmId,
  });

  const { data: algorithmSources, isFetching: isFetchingSources } =
    useGetAlgorithmSourcesQuery({
      id: algorithmId,
    });

  const { data: algorithmMappings, isFetching: isFetchingMappings } =
    useGetAlgorithmMappingsQuery({
      id: algorithmId,
    });

  useEffect(() => {
    onLoadingStateChange?.(
      isFetchingFilterClause || isFetchingSources || isFetchingMappings,
    );
  }, [
    isFetchingFilterClause,
    isFetchingSources,
    isFetchingMappings,
    onLoadingStateChange,
  ]);

  const { mutateAsync } = useMutateAlgorithmFilterClauseQuery();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);
  const { resetAlert, setSuccess } = useAlertContext();

  const onSuccess = useCallback(() => {
    void refetch();
    onClose?.(true);
    setSuccess();
  }, [refetch, onClose, setSuccess]);

  const handleSubmit = useCallback(
    (values: AlgorithmFilterClauseValidFormDataType, confirm: boolean) => {
      resetAlert();
      clearMutationErrors();
      return mutateAsync({
        id: algorithmId,
        data: { ...values, algorithmId },
        headers: confirmationHeaders(confirm),
      })
        .then(onSuccess)
        .catch((error) => {
          setMutationErrors(error?.response?.data?.errors ?? []);
        });
    },
    [
      resetAlert,
      clearMutationErrors,
      mutateAsync,
      algorithmId,
      onSuccess,
      setMutationErrors,
    ],
  );

  if (isFetchingFilterClause || isFetchingSources || isFetchingMappings) {
    return null;
  }

  return (
    <Form
      schema={AlgorithmFilterClauseValidFormDataSchema}
      defaultValues={algorithmFilterClause}
      onSubmit={handleSubmit}
      serverErrors={mutationErrors}
    >
      <AlgorithmFilterClauseFormModalInner
        sources={algorithmSources?.sources ?? []}
        mappings={algorithmMappings?.mappings ?? []}
        onClose={() => onClose?.(false)}
      />
    </Form>
  );
};

function AlgorithmFilterClauseFormModalInner({
  sources,
  mappings,
  onClose,
}: {
  sources: AlgorithmSourceFormDataType[];
  mappings: AlgorithmMappingQueryDtoType[];
  onClose?: () => void;
}) {
  const { isDirty } = useFormState();
  const handleCancel = useCallback(() => {
    if (isDirty) {
      confirmPanelFormClose(onClose);
    } else {
      onClose?.();
    }
  }, [isDirty, onClose]);

  return (
    <Modal
      footer={
        <ModalFooter>
          <Flex gap="small" justify="end">
            <ActionButton type="abort" onClick={handleCancel} />
            <ActionButton type="formSubmit" />
          </Flex>
        </ModalFooter>
      }
      onCancel={onClose}
      width="70rem"
    >
      <ModalHeader>
        <ModalTitle>{t(`title`)}</ModalTitle>
      </ModalHeader>
      <FormAlerts />
      <Padding scrollable>
        <AlgorithmConditionForm
          name="filterClause"
          alwaysShowActions={true}
          viewType={AlgorithmElementFunctionViewType.F}
          sources={sources}
          mappings={mappings}
        />
      </Padding>
    </Modal>
  );
}
